import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { GameStats } from "../../types";

interface ResultOverlayProps {
  won: boolean | null;
  stats: GameStats | null;
  onRetry: () => void;
  onExit?: () => void;
}

interface StatRowProps {
  label: string;
  value: string;
}

const StatRow = React.memo(function StatRow({ label, value }: StatRowProps) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-gray-400 tracking-widest text-xs uppercase">
        {label}
      </span>
      <span className="font-mono font-bold text-white">{value}</span>
    </div>
  );
});

export const ResultOverlay = React.memo(function ResultOverlay({
  won,
  stats,
  onRetry,
  onExit,
}: ResultOverlayProps) {
  const visible = won !== null && stats !== null;

  const overlayStyle = useMemo(
    () => ({
      backgroundColor: "rgba(0,0,0,0.42)",
    }),
    [],
  );

  if (!visible) return null;

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center rounded-xl z-20"
      style={overlayStyle}
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <div
        className="flex flex-col gap-5 p-6 rounded-2xl border w-72"
        style={{
          background: won
            ? "linear-gradient(135deg, #052e16 0%, #111827 100%)"
            : "linear-gradient(135deg, #2d0a0a 0%, #111827 100%)",
          borderColor: won ? "#4ade8040" : "#f8717140",
        }}
      >
        {/* Icon + title */}
        <div className="flex flex-col items-center gap-2">
          <div className="text-5xl">{won ? "⚡" : "💀"}</div>
          <div
            className="font-bold text-lg tracking-widest uppercase"
            style={{ color: won ? "#4ade80" : "#f87171" }}
          >
            {won ? "Power Restored" : "Circuit Failed"}
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-col gap-2.5 bg-black/30 rounded-xl p-4">
          <StatRow label="Clicks" value={String(stats!.totalClicks)} />
          {won && (
            <>
              <StatRow label="Time elapsed" value={`${stats!.timeElapsed}s`} />
              <StatRow
                label="Time remaining"
                value={`${stats!.timeRemaining}s`}
              />
            </>
          )}
          <StatRow label="Difficulty" value={stats!.difficulty.toUpperCase()} />
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onRetry}
            className="flex-1 py-2.5 rounded-lg font-bold text-sm tracking-widest uppercase transition-all active:scale-95"
            style={{
              background: won ? "#4ade8022" : "#D9770622",
              border: `1px solid ${won ? "#4ade8080" : "#D9770680"}`,
              color: won ? "#4ade80" : "#D97706",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = won
                ? "#4ade8033"
                : "#D9770633";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = won
                ? "#4ade8022"
                : "#D9770622";
            }}
          >
            Retry
          </button>
          {onExit && (
            <button
              onClick={onExit}
              className="flex-1 py-2.5 rounded-lg font-bold text-sm tracking-widest uppercase
                    bg-gray-700/50 border border-gray-600 text-gray-300
                    hover:bg-gray-700 transition-all active:scale-95"
            >
              Exit
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
});
